<script>
  import { tick } from 'svelte';
  import { fromLonLat } from 'ol/proj';
  import { mapStore } from '../stores/map.js';
  import { nominatimFetch } from '../lib/nominatim.js';
  import { rankSearchHits, searchArea } from '../lib/searchRanking.js';
  import { Search, Loader2, X, MapPinPlus } from 'lucide-svelte';
  import { startReport } from '../stores/reportMissing.js';
  import { cn } from '../lib/utils.js';
  import { _ } from 'svelte-i18n';

  /** Bounding box [minLon, minLat, maxLon, maxLat] to restrict Nominatim search. */
  export let regionExtent = null;

  let query = '';
  let searching = false;
  let results = [];
  let showResults = false;
  // True after a search that found nothing: the box then offers to report a
  // missing playground (#1000). Cleared by the next edit.
  let noHits = false;
  // Set by Escape, cleared by the next edit: a search that lands after the
  // visitor dismissed the list fills the cache but must not reopen it.
  let dismissed = false;
  let inputEl;
  let resultsEl;

  /** Index of the option named by `aria-activedescendant`; -1 = none active. */
  let activeIndex = -1;

  const idPrefix = `searchbar-${instanceCounter++}`;
  const listboxId = `${idPrefix}-listbox`;
  const optionId = (i) => `${idPrefix}-option-${i}`;

  $: listOpen = showResults && results.length > 0;
  $: activeId = listOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined;
  $: void scrollActiveIntoView(activeIndex, listOpen);

  async function scrollActiveIntoView(i, open) {
    if (!open || i < 0) return;
    await tick();
    resultsEl?.children?.[i]?.scrollIntoView({ block: 'nearest' });
  }

  // Each search gets a number; an answer that lands after a newer search
  // started is dropped, so a slow request for "Frank" cannot overwrite the
  // results for "Frankfurt" (two requests per search widen that window).
  let searchSeq = 0;

  async function search() {
    const seq = ++searchSeq;
    const q = query.trim();
    if (!q) {
      results = [];
      activeIndex = -1;
      showResults = false;
      return;
    }
    searching = true;
    try {
      const baseParams = { q, addressdetails: 1, limit: 10 };
      // Both answers, always (#973): the one bounded to the view finds the
      // nearby street or shop, the unbounded one the city. Using the local
      // answer alone whenever it had anything hid every city behind a
      // same-named shop. The view still biases the unbounded query.
      let globalHits = [];
      let localHits = null;   // null = no view, or the view request failed
      // The in-view query reaches at least ~10 km from the view centre, so a
      // street-level view still finds the street across town (#976). The
      // wider box holds more matches, so it asks for more of them; the ranking
      // keeps the real view and sorts local hits by distance.
      const area = searchArea(regionExtent);
      if (area) {
        const viewbox = area.join(',');
        const [g, l] = await Promise.allSettled([
          nominatimFetch('/search', { ...baseParams, viewbox, bounded: 0 }, { timeout: 0 }),
          nominatimFetch('/search', { ...baseParams, viewbox, bounded: 1, limit: 25 }, { timeout: 0 }),
        ]);
        // One failed request must not hide what the other found — but it is
        // logged, so an empty search box has a trace.
        if (g.status === 'rejected' && l.status === 'rejected') throw g.reason;
        if (g.status === 'rejected') console.warn('Search (unbounded) failed:', g.reason);
        if (l.status === 'rejected') console.warn('Search (in view) failed:', l.reason);
        globalHits = g.status === 'fulfilled' ? g.value : [];
        localHits = l.status === 'fulfilled' ? l.value : null;
      } else {
        globalHits = await nominatimFetch('/search', baseParams, { timeout: 0 });
      }
      if (seq !== searchSeq) return;
      results = rankSearchHits(globalHits, localHits, area ? regionExtent : null);
      activeIndex = -1;
      showResults = results.length > 0 && !dismissed;
      noHits = results.length === 0 && !dismissed;
    } catch (err) {
      if (seq !== searchSeq) return;
      console.error('Search failed:', err);
      results = [];
      activeIndex = -1;
      showResults = false;
    } finally {
      if (seq === searchSeq) searching = false;
    }
  }

  function selectResult(result) {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    const coord = fromLonLat([lon, lat]);
    $mapStore?.getView().animate({ center: coord, zoom: 17 });
    query = result.display_name.split(',')[0];
    showResults = false;
    // Discard the hits, don't just hide them. `onFocus` reopens the list
    // whenever `results` is non-empty, and selecting never moves DOM focus out
    // of the input (see onResultsMousedown), so merely hiding left the list
    // free to spring straight back — observed on Android: tap a suggestion,
    // the map zooms, the list is still sitting there. They are also stale by
    // then: `query` has been replaced with the chosen name, so they are no
    // longer suggestions for what the input says.
    results = [];
    activeIndex = -1;

    // Drop the on-screen keyboard on touch. Keeping DOM focus in the input is
    // right on desktop — it is the whole reason onResultsMousedown suppresses
    // the blur — but on a phone a focused input means the keyboard covers the
    // lower half of the screen, hiding the place the map has just flown to.
    //
    // TRADE-OFF: the APG combobox pattern says focus should stay on the input
    // after selection, and a touch screen-reader user loses their position in
    // the page when it does not. Weighed against every sighted touch user
    // having to dismiss a keyboard by hand on every search, and resolved in
    // their favour. Revisit if it proves the wrong call.
    if (window.matchMedia?.('(pointer: coarse)').matches) inputEl?.blur();
  }

  function onKeydown(e) {
    if (e.key === 'ArrowDown') {
      // preventDefault only once the key is actually consumed. In a
      // single-line input ArrowUp/ArrowDown natively jump the caret to the
      // start/end of the value; swallowing them with no list open would take
      // that away from a keyboard user editing a long query.
      if (results.length === 0) return;
      e.preventDefault();
      // Reopen a list that was dismissed while its results are still cached.
      if (!showResults) {
        showResults = true;
        return;
      }
      activeIndex = activeIndex >= results.length - 1 ? 0 : activeIndex + 1;
      return;
    }
    if (e.key === 'ArrowUp') {
      if (!listOpen) return;   // see ArrowDown: leave the caret keys alone
      e.preventDefault();
      activeIndex = activeIndex <= 0 ? results.length - 1 : activeIndex - 1;
      return;
    }
    if (e.key === 'Enter') {
      if (listOpen && activeIndex >= 0) {
        e.preventDefault();
        selectResult(results[activeIndex]);
      } else {
        search();
      }
      return;
    }
    if (e.key === 'Escape') {
      // Keep focus in the input, and keep the window-level Escape handlers
      // (PlaygroundPanel close, Map popup) from also firing on this press.
      if (listOpen) e.stopPropagation();
      // A search still waiting out the debounce would otherwise reopen the
      // list on its own half a second later (#908).
      clearTimeout(searchTimeout);
      dismissed = true;
      showResults = false;
      activeIndex = -1;
    }
  }

  function onInput() {
    // Editing the query invalidates the highlighted option immediately, not
    // 450 ms later when the new results land. D7 resets `activeIndex` on every
    // reassignment of `results`, but `search()` is gated on a two-character
    // query, so that reset never fires on the way back down: typing "Fulda",
    // arrowing to a suggestion and backspacing to "F" left the cached list
    // open with row 0 still active, and Enter then navigated to a suggestion
    // for a query the user had already deleted.
    activeIndex = -1;
    dismissed = false;
    noHits = false;

    // Below the search threshold there is nothing the cached list can be
    // suggestions *for*, so it must not keep claiming to be — `aria-expanded`
    // has to describe the popup that is actually relevant to the input.
    if (query.length < 2) {
      searchSeq++;   // an answer still in flight must not reopen the list
      results = [];
      showResults = false;
    }

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      if (query.length >= 2) search();
    }, 450);
  }

  let searchTimeout;

  function clearSearch() {
    searchSeq++;   // an answer still in flight must not refill a cleared box
    query = '';
    results = [];
    activeIndex = -1;
    showResults = false;
    noHits = false;
    inputEl?.focus();
  }

  function onFocus() {
    if (results.length > 0) showResults = true;
  }

  /**
   * Dismissal is focus containment, not a timer: the list closes only once
   * focus leaves the whole card. A timer raced both a mouse click on a result
   * (Safari) and a touch scroll inside the list (iOS).
   */
  function onCardFocusOut(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    showResults = false;
    noHits = false;
    activeIndex = -1;
  }

  /**
   * Deliberately `mousedown`, not `pointerdown`: on touch, `mousedown` is
   * synthesised after the tap completes, so suppressing it cannot interfere
   * with scrolling the list. It keeps the input from blurring on click.
   */
  function onResultsMousedown(e) {
    e.preventDefault();
  }
</script>

<div class="search-card" onfocusout={onCardFocusOut}>
  <div class="search-input-wrapper">
    <div class="search-icon">
      {#if searching}
        <Loader2 class="h-5 w-5 text-gray-400 animate-spin" />
      {:else}
        <Search class="h-5 w-5 text-gray-400" />
      {/if}
    </div>
    <input
      bind:this={inputEl}
      type="text"
      class="search-input"
      placeholder={$_('search.placeholder')}
      bind:value={query}
      onkeydown={onKeydown}
      oninput={onInput}
      onfocus={onFocus}
      aria-label={$_('search.ariaLabel')}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={listOpen}
      aria-controls={listboxId}
      aria-activedescendant={activeId}
      aria-busy={searching}
    />
    {#if query}
      <button class="clear-btn" onclick={clearSearch} aria-label={$_('search.clearLabel')}>
        <X class="h-4 w-4 text-gray-400" />
      </button>
    {/if}
  </div>

  {#if noHits && !listOpen}
    <!-- mousedown is suppressed like the results list's: Safari and Firefox
         on macOS do not focus a button on click, so the input's blur would
         close this offer (onCardFocusOut) before the click arrives. -->
    <div class="no-hits" role="status" onmousedown={(e) => e.preventDefault()}>
      <span>{$_('search.notFound')}</span>
      <button type="button" class="no-hits-report" onclick={() => { noHits = false; startReport(); }}>
        <MapPinPlus class="h-4 w-4" aria-hidden="true" />
        {$_('report.searchHint')}
      </button>
    </div>
  {/if}

  {#if listOpen}
    <div
      class="search-results"
      bind:this={resultsEl}
      id={listboxId}
      role="listbox"
      aria-label={$_('search.ariaLabel')}
      tabindex="-1"
      onmousedown={onResultsMousedown}
    >
      {#each results as result, i}
        <!--
          Options stay real buttons: `role="option"` overrides the native
          role, while the button keeps working for touch screen readers,
          whose virtual cursor activates options directly and never follows
          `aria-activedescendant`. `tabindex="-1"` keeps them out of the tab
          order — arrow keys, not Tab, reach them on desktop.
        -->
        <button
          class="result-item"
          class:active={i === activeIndex}
          id={optionId(i)}
          role="option"
          aria-selected={i === activeIndex}
          tabindex="-1"
          onclick={() => selectResult(result)}
          onmousemove={() => (activeIndex = i)}
        >
          <MapPin class="h-4 w-4 text-gray-400 shrink-0" />
          <span class="result-text">{result.display_name}</span>
        </button>
      {/each}
    </div>
  {/if}
</div>

<script context="module">
  import { MapPin } from 'lucide-svelte';

  /**
   * Element ids in the combobox contract must be unique per instance, and the
   * component is in legacy mode so `$props.id()` is unavailable. A
   * module-scoped counter gives every mounted instance its own id prefix.
   */
  let instanceCounter = 0;
</script>

<style>
  .no-hits {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    border-top: 1px solid #e5e7eb;
    font-size: 0.8125rem;
    color: #4b5563;
  }

  .no-hits-report {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.625rem;
    border: none;
    border-radius: 999px;
    background: #ecfdf5;
    color: #065f46;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  .no-hits-report:hover {
    background: #d1fae5;
  }

  .search-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    width: 300px;
    max-width: calc(100vw - 5rem);
  }

  .search-input-wrapper {
    display: flex;
    align-items: center;
    padding: 0 12px;
    height: 48px;
  }

  .search-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 15px;
    background: transparent;
    color: #202124;
  }

  .search-input::placeholder {
    color: #9aa0a6;
  }

  .search-input:disabled {
    opacity: 0.6;
  }

  .clear-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    margin: -8px;
    margin-left: 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 50%;
  }

  .clear-btn:hover {
    background: #f1f3f4;
  }

  .search-results {
    border-top: 1px solid #e8eaed;
    max-height: 280px;
    overflow-y: auto;
  }

  .result-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    width: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
  }

  /*
   * `.active` is the single highlight, written by both arrow keys and
   * pointer movement. A separate `:hover` rule would let the pointer
   * highlight one row while `aria-activedescendant` named another.
   */
  .result-item.active {
    background: #f1f3f4;
  }

  .result-text {
    font-size: 14px;
    color: #202124;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Nominatim display_name values are long, and the search card is narrowed
     further on mobile — a single ellipsised line makes results hard to tell
     apart. Wrap to two lines below the mobile breakpoint. */
  @media (max-width: 1023px) {
    .result-item {
      align-items: flex-start;
    }

    .result-text {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
      text-overflow: clip;
    }
  }
</style>
